import { supabase } from './supabase';
import { dailyTrackingService } from './dailyTrackingService';
import { myMedicationService } from './myMedicationService';
import { isMedicationActive } from '../utils/dateHelpers';
import type { DailyTracking } from '../types';
import type { MyMedication } from '../types';

// ============= TYPY =============

export interface DailyData {
  date: string;
  bm_count: number;
  has_blood: boolean;
  has_mucus: boolean;
  avg_urgency: number;
  avg_pain: number;
  gurgling?: number;
  bloating?: number;
  stress_level?: number;
  sleep_hours?: number;
  food_heaviness?: number;
  alcohol_level?: number;
  food_change?: number;
  bowelLoad?: number;
}

export interface StatusClassification {
  status: 'ZAOSTRZENIE' | 'POGARSZA SIĘ' | 'STABILNIE';
  color: 'red' | 'yellow' | 'green';
}

export interface TriggerCount {
  key: string;
  label: string;
  count: number;
  total: number;
}

export interface MedicationImpact {
  medication: MyMedication;
  before14Days: {
    avgBm: number;
    bloodDays: number;
    avgLoad: number;
    daysWithData: number;
  };
  after30Days: {
    avgBm: number;
    bloodDays: number;
    avgLoad: number;
    daysWithData: number;
  };
  summary: 'poprawa' | 'pogorszenie' | 'bez-wielkich-zmian';
  hasEnoughBefore: boolean;
}

export interface DataQuality {
  period: string;
  bmDays: number;
  totalDays: number;
  bmCoverage: number;
  trackingDays: number;
  trackingCoverage: number;
  assessment: 'wystarczajaco' | 'srednio' | 'za-malo';
  assessmentText: string;
}

export interface HealthReportData {
  dateFrom: string;
  dateTo: string;
  dailyData: DailyData[];
  status: StatusClassification;
  last7AvgLoad: number;
  bloodDaysLast7: number;
  trend: string;
  trendDetails: string[];
  pogorszenieDays: string[];
  triggers: {
    objawy: TriggerCount[];
    czynniki: TriggerCount[];
  };
  medications: MedicationImpact[];
  dataQuality: DataQuality;
  warnings: string[];
}

// ============= HELPERY =============

function addDays(date: string, days: number): string {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export function calculateBowelLoad(day: DailyData): number {
  let load = 0;
  load += day.bm_count * 3;
  load += day.has_blood ? 15 : 0;
  load += day.has_mucus ? 10 : 0;
  load += day.avg_urgency * 3;
  load += day.avg_pain * 3;
  const gurgling = day.gurgling ?? 0;
  load += gurgling * 3;
  return Math.min(load, 100);
}

export function classifyStatus(avgLoad: number, bloodDays: number): StatusClassification {
  if (avgLoad >= 46 || bloodDays >= 3) {
    return { status: 'ZAOSTRZENIE', color: 'red' };
  }
  if (avgLoad >= 26) {
    return { status: 'POGARSZA SIĘ', color: 'yellow' };
  }
  return { status: 'STABILNIE', color: 'green' };
}

export function calculateTrend(
  last7Avg: number,
  prev7Avg: number,
  last7Bm: number,
  prev7Bm: number,
  last7Blood: number,
  prev7Blood: number,
  last7Urgency: number,
  prev7Urgency: number
): { trend: string; details: string[] } {
  const details: string[] = [];
  if (prev7Avg === 0) {
    return {
      trend: 'Bez większych zmian',
      details: ['Za mało danych do porównania.']
    };
  }
  const changePct = ((last7Avg - prev7Avg) / prev7Avg) * 100;
  if (changePct > 20) {
    if (last7Bm > prev7Bm) details.push('więcej wypróżnień');
    if (last7Blood > prev7Blood) details.push('częściej krew');
    if (last7Urgency > prev7Urgency) details.push('większa pilność');
    return {
      trend: details.length > 0 ? `Objawy nasilają się – ${details.join(', ')}.` : 'Objawy nasilają się',
      details
    };
  }
  if (changePct < -20) {
    if (last7Bm < prev7Bm) details.push('mniej wypróżnień');
    if (last7Blood < prev7Blood) details.push('rzadziej krew');
    if (last7Urgency < prev7Urgency) details.push('mniejsza pilność');
    return {
      trend: details.length > 0 ? `Objawy słabną – ${details.join(', ')}.` : 'Objawy słabną',
      details
    };
  }
  return {
    trend: 'Bez większych zmian',
    details: details.length > 0 ? details : ['Porównanie ostatnich 7 vs poprzednich 7 dni bez istotnych różnic.']
  };
}

export type ValueLevel = 'niski' | 'średni' | 'wysoki';

export function mapValueToText(value: number): ValueLevel {
  if (value <= 1) return 'niski';
  if (value <= 3) return 'średni';
  return 'wysoki';
}

function detectPogorszenieDay(
  day: DailyData,
  allDays: DailyData[],
  dateFrom: string
): boolean {
  const dayIndex = allDays.findIndex(d => d.date === day.date);
  if (dayIndex < 6) return false;
  const prev7 = allDays.slice(Math.max(0, dayIndex - 7), dayIndex);
  const avgBm7 = prev7.length > 0
    ? prev7.reduce((s, d) => s + d.bm_count, 0) / prev7.length
    : day.bm_count;
  const avgUrgency7 = prev7.filter(d => d.bm_count > 0).length > 0
    ? prev7.filter(d => d.bm_count > 0).reduce((s, d) => s + d.avg_urgency, 0) / prev7.filter(d => d.bm_count > 0).length
    : 0;
  const avgPain7 = prev7.filter(d => d.bm_count > 0).length > 0
    ? prev7.filter(d => d.bm_count > 0).reduce((s, d) => s + d.avg_pain, 0) / prev7.filter(d => d.bm_count > 0).length
    : 0;
  const avgGurgling7 = prev7.length > 0
    ? prev7.reduce((s, d) => s + (d.gurgling ?? 0), 0) / prev7.length
    : 0;

  const prevDay = addDays(day.date, -1);
  const prevDayData = allDays.find(d => d.date === prevDay);
  const bloodAppeared = day.has_blood && !(prevDayData?.has_blood ?? false);

  let count = 0;
  if (day.bm_count >= avgBm7 + 3) count++;
  if (bloodAppeared) count++;
  if (day.bm_count > 0 && day.avg_urgency >= avgUrgency7 + 2) count++;
  if (day.bm_count > 0 && day.avg_pain >= avgPain7 + 2) count++;
  if ((day.gurgling ?? 0) >= avgGurgling7 + 2) count++;

  return count >= 2;
}

function analyzeTriggers(
  pogorszenieDates: string[],
  allDays: DailyData[]
): { objawy: TriggerCount[]; czynniki: TriggerCount[] } {
  const objawyCounts: Record<string, number> = {
    gurgling_high: 0,
    bloating_high: 0,
    mucus: 0,
    urgency_up: 0
  };
  const czynnikiCounts: Record<string, number> = {
    sleep_low: 0,
    stress_high: 0,
    food_heavy: 0,
    alcohol_high: 0,
    food_change: 0
  };

  const objawyLabels: Record<string, string> = {
    gurgling_high: 'Przelewanie >3',
    bloating_high: 'Wzdęcia >3',
    mucus: 'Śluz',
    urgency_up: 'Pilność wzrosła'
  };
  const czynnikiLabels: Record<string, string> = {
    sleep_low: 'Sen <6h',
    stress_high: 'Stres >3',
    food_heavy: 'Ciężkie jedzenie >3',
    alcohol_high: 'Alkohol >2',
    food_change: 'Zmiana diety >2'
  };

  for (const pogDate of pogorszenieDates) {
    const pogDay = allDays.find(d => d.date === pogDate);
    if (!pogDay) continue;

    let seenGurgling = false;
    let seenBloating = false;
    let seenMucus = false;
    let seenUrgencyUp = false;
    let seenSleep = false;
    let seenStress = false;
    let seenFoodHeavy = false;
    let seenAlcohol = false;
    let seenFoodChange = false;

    for (let lag = 1; lag <= 3; lag++) {
      const checkDate = addDays(pogDate, -lag);
      const checkDay = allDays.find(d => d.date === checkDate);
      if (!checkDay) continue;

      if ((checkDay.gurgling ?? 0) > 3) seenGurgling = true;
      if ((checkDay.bloating ?? 0) > 3) seenBloating = true;
      if (checkDay.has_mucus) seenMucus = true;
      if (checkDay.bm_count > 0 && pogDay.bm_count > 0 && checkDay.avg_urgency < pogDay.avg_urgency) seenUrgencyUp = true;
      if ((checkDay.sleep_hours ?? 8) < 6) seenSleep = true;
      if ((checkDay.stress_level ?? 0) > 3) seenStress = true;
      if ((checkDay.food_heaviness ?? 0) > 3) seenFoodHeavy = true;
      if ((checkDay.alcohol_level ?? 0) > 2) seenAlcohol = true;
      if ((checkDay.food_change ?? 0) > 2) seenFoodChange = true;
    }

    if (seenGurgling) objawyCounts.gurgling_high++;
    if (seenBloating) objawyCounts.bloating_high++;
    if (seenMucus) objawyCounts.mucus++;
    if (seenUrgencyUp) objawyCounts.urgency_up++;
    if (seenSleep) czynnikiCounts.sleep_low++;
    if (seenStress) czynnikiCounts.stress_high++;
    if (seenFoodHeavy) czynnikiCounts.food_heavy++;
    if (seenAlcohol) czynnikiCounts.alcohol_high++;
    if (seenFoodChange) czynnikiCounts.food_change++;
  }

  const total = pogorszenieDates.length;
  const objawy: TriggerCount[] = Object.entries(objawyCounts).map(([key, count]) => ({
    key,
    label: objawyLabels[key] ?? key,
    count,
    total
  }));
  const czynniki: TriggerCount[] = Object.entries(czynnikiCounts).map(([key, count]) => ({
    key,
    label: czynnikiLabels[key] ?? key,
    count,
    total
  }));

  return { objawy, czynniki };
}

function analyzeMedicationImpact(
  medication: MyMedication,
  allDays: DailyData[]
): MedicationImpact {
  const startDate = medication.startDate;
  const beforeStart = addDays(startDate, -1);
  const beforeFrom = addDays(startDate, -14);
  const afterTo = addDays(startDate, 30);

  const beforeDays = allDays.filter(d => d.date >= beforeFrom && d.date <= beforeStart);
  const afterDays = allDays.filter(d => d.date >= startDate && d.date <= afterTo);

  const beforeBmTotal = beforeDays.reduce((s, d) => s + d.bm_count, 0);
  const beforeBloodDays = beforeDays.filter(d => d.has_blood).length;
  const beforeLoadAvg = beforeDays.length > 0
    ? beforeDays.reduce((s, d) => s + (d.bowelLoad ?? calculateBowelLoad(d)), 0) / beforeDays.length
    : 0;

  const afterBmTotal = afterDays.reduce((s, d) => s + d.bm_count, 0);
  const afterBloodDays = afterDays.filter(d => d.has_blood).length;
  const afterLoadAvg = afterDays.length > 0
    ? afterDays.reduce((s, d) => s + (d.bowelLoad ?? calculateBowelLoad(d)), 0) / afterDays.length
    : 0;

  const beforeAvgBm = beforeDays.length > 0 ? beforeBmTotal / beforeDays.length : 0;
  const afterAvgBm = afterDays.length > 0 ? afterBmTotal / afterDays.length : 0;

  const hasEnoughBefore = beforeDays.length >= 7;
  let summary: 'poprawa' | 'pogorszenie' | 'bez-wielkich-zmian' = 'bez-wielkich-zmian';
  if (afterAvgBm < beforeAvgBm - 0.5 && afterBloodDays <= beforeBloodDays && afterLoadAvg < beforeLoadAvg - 5) {
    summary = 'poprawa';
  } else if (afterAvgBm > beforeAvgBm + 0.5 || afterBloodDays > beforeBloodDays || afterLoadAvg > beforeLoadAvg + 5) {
    summary = 'pogorszenie';
  }

  return {
    medication,
    before14Days: {
      avgBm: beforeAvgBm,
      bloodDays: beforeBloodDays,
      avgLoad: beforeLoadAvg,
      daysWithData: beforeDays.length
    },
    after30Days: {
      avgBm: afterAvgBm,
      bloodDays: afterBloodDays,
      avgLoad: afterLoadAvg,
      daysWithData: afterDays.length
    },
    summary,
    hasEnoughBefore
  };
}

function calculateDataQuality(
  bmDays: number,
  totalDays: number,
  trackingDays: number
): DataQuality {
  const bmCoverage = totalDays > 0 ? (bmDays / totalDays) * 100 : 0;
  const trackingCoverage = totalDays > 0 ? (trackingDays / totalDays) * 100 : 0;
  const bmPct = Math.round(bmCoverage);
  const trackPct = Math.round(trackingCoverage);

  let assessment: 'wystarczajaco' | 'srednio' | 'za-malo' = 'wystarczajaco';
  let assessmentText = 'wystarczająco danych do analizy.';
  const minCoverage = Math.min(bmCoverage, trackingCoverage);
  if (minCoverage >= 80) {
    assessment = 'wystarczajaco';
    assessmentText = 'wystarczająco danych do analizy.';
  } else if (minCoverage >= 50) {
    assessment = 'srednio';
    assessmentText = 'średnio danych – wnioski orientacyjne';
  } else {
    assessment = 'za-malo';
    assessmentText = 'za mało danych – raport niepełny';
  }

  return {
    period: `ostatnie ${totalDays} dni`,
    bmDays,
    totalDays,
    bmCoverage: bmPct,
    trackingDays,
    trackingCoverage: trackPct,
    assessment,
    assessmentText
  };
}

// ============= GŁÓWNA FUNKCJA =============

export const healthReportService = {
  async getHealthReport(userId: string, dateFrom: string, dateTo: string): Promise<HealthReportData> {
    const warnings: string[] = [];

    const { data: movements, error } = await supabase
      .from('bowel_movements')
      .select('*')
      .eq('user_id', userId)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date', { ascending: true });

    if (error) throw error;

    let trackingList: DailyTracking[] = [];
    try {
      trackingList = await dailyTrackingService.getRange(userId, dateFrom, dateTo);
    } catch {
      warnings.push('Brak danych z samopoczucia – analizuję tylko wypróżnienia.');
    }

    let medications: MyMedication[] = [];
    try {
      medications = (await myMedicationService.getAll()).filter(m => isMedicationActive(m));
    } catch {
      warnings.push('Nie udało się pobrać leków.');
    }

    const trackingByDate: Record<string, DailyTracking> = {};
    for (const t of trackingList) {
      trackingByDate[t.date] = t;
    }

    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);
    const days: string[] = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    const bmByDate: Record<string, { count: number; blood: boolean; mucus: boolean; urgency: number[]; pain: number[] }> = {};
    for (const d of days) bmByDate[d] = { count: 0, blood: false, mucus: false, urgency: [], pain: [] };

    for (const m of movements || []) {
      const dt = (m as any).date;
      if (!bmByDate[dt]) continue;
      bmByDate[dt].count++;
      if ((m as any).has_blood) bmByDate[dt].blood = true;
      if ((m as any).has_mucus) bmByDate[dt].mucus = true;
      bmByDate[dt].urgency.push((m as any).urgency ?? 1);
      bmByDate[dt].pain.push((m as any).pain ?? 0);
    }

    const dailyData: DailyData[] = days.map(date => {
      const bm = bmByDate[date];
      const dt = trackingByDate[date];
      const bmCount = bm?.count ?? 0;
      const avgUrgency = bm?.urgency?.length ? bm.urgency.reduce((s, u) => s + u, 0) / bm.urgency.length : 0;
      const avgPain = bm?.pain?.length ? bm.pain.reduce((s, p) => s + p, 0) / bm.pain.length : 0;
      const day: DailyData = {
        date,
        bm_count: bmCount,
        has_blood: bm?.blood ?? false,
        has_mucus: bm?.mucus ?? false,
        avg_urgency: avgUrgency,
        avg_pain: avgPain,
        gurgling: dt?.gurgling,
        bloating: dt?.bloating,
        stress_level: dt?.stressLevel,
        sleep_hours: dt?.sleepHours,
        food_heaviness: dt?.foodHeaviness,
        alcohol_level: dt?.alcoholLevel,
        food_change: dt?.foodChange
      };
      day.bowelLoad = calculateBowelLoad(day);
      return day;
    });

    const sortedData = [...dailyData].sort((a, b) => a.date.localeCompare(b.date));
    const last7 = sortedData.slice(-7);
    const prev7 = sortedData.length >= 14 ? sortedData.slice(-14, -7) : [];

    const last7AvgLoad = last7.length > 0 ? last7.reduce((s, d) => s + (d.bowelLoad ?? 0), 0) / last7.length : 0;
    const bloodDaysLast7 = last7.filter(d => d.has_blood).length;
    const prev7AvgLoad = prev7.length > 0 ? prev7.reduce((s, d) => s + (d.bowelLoad ?? 0), 0) / prev7.length : 0;

    const last7Bm = last7.reduce((s, d) => s + d.bm_count, 0) / Math.max(1, last7.length);
    const prev7Bm = prev7.reduce((s, d) => s + d.bm_count, 0) / Math.max(1, prev7.length);
    const last7Blood = last7.filter(d => d.has_blood).length;
    const prev7Blood = prev7.filter(d => d.has_blood).length;
    const last7Urgency = last7.filter(d => d.bm_count > 0).length > 0
      ? last7.filter(d => d.bm_count > 0).reduce((s, d) => s + d.avg_urgency, 0) / last7.filter(d => d.bm_count > 0).length
      : 0;
    const prev7Urgency = prev7.filter(d => d.bm_count > 0).length > 0
      ? prev7.filter(d => d.bm_count > 0).reduce((s, d) => s + d.avg_urgency, 0) / prev7.filter(d => d.bm_count > 0).length
      : 0;

    const { trend, details } = calculateTrend(last7AvgLoad, prev7AvgLoad, last7Bm, prev7Bm, last7Blood, prev7Blood, last7Urgency, prev7Urgency);
    const status = classifyStatus(last7AvgLoad, bloodDaysLast7);

    const pogorszenieDays = sortedData.filter(d => detectPogorszenieDay(d, sortedData, dateFrom)).map(d => d.date);

    let triggers = { objawy: [] as TriggerCount[], czynniki: [] as TriggerCount[] };
    if (sortedData.length >= 14) {
      if (pogorszenieDays.length >= 5) {
        triggers = analyzeTriggers(pogorszenieDays, sortedData);
      } else {
        warnings.push('Za mało przypadków pogorszenia do analizy wzorców.');
      }
    } else {
      warnings.push('Za mało dni do analizy wzorców (potrzeba min. 14 dni)');
    }

    const medicationImpacts: MedicationImpact[] = medications.map(med =>
      analyzeMedicationImpact(med, sortedData)
    );

    const bmDaysCount = dailyData.filter(d => d.bm_count > 0).length;
    const trackingDaysCount = dailyData.filter(d => trackingByDate[d.date]).length;
    const dataQuality = calculateDataQuality(bmDaysCount, days.length, trackingDaysCount);

    return {
      dateFrom,
      dateTo,
      dailyData: sortedData,
      status,
      last7AvgLoad,
      bloodDaysLast7,
      trend,
      trendDetails: details,
      pogorszenieDays,
      triggers,
      medications: medicationImpacts,
      dataQuality,
      warnings
    };
  }
};
